import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-expenses';
import { formatDateTime as formatWhen } from '../format-date';

type HistoryEntry =
  paths['/v1/expenses/{expenseId}/history']['get']['responses'][200]['content']['application/json']['items'][number];

type HistoryState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; items: readonly HistoryEntry[] };

/** #915 C4: a `class-set` entry's change, «PG» from «nessuna»; null for any other entry. */
function classSetOf(entry: HistoryEntry): { from: string | null; to: string | null } | null {
  if ((entry.action as string) !== 'class-set') return null;
  const change = (entry as { internalClass?: { from?: unknown; to?: unknown } }).internalClass;
  const code = (value: unknown) => (typeof value === 'string' ? value : null);
  return { from: code(change?.from), to: code(change?.to) };
}

function HistoryBody({ state, t }: { readonly state: HistoryState; readonly t: T }) {
  const { t: tp } = useTranslation('expenses-parity');
  if (state.status === 'loading') return <Skeleton className="h-10 w-full" />;
  if (state.status === 'error') {
    return <p className="text-muted-foreground text-sm">{t('detail.history.loadError')}</p>;
  }
  if (state.items.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('detail.history.empty')}</p>;
  }
  return (
    <ol className="flex flex-col gap-2 text-sm">
      {state.items.map((entry, index) => {
        const classSet = classSetOf(entry);
        const none = tp('class.none');
        return (
          <li key={`${entry.at}-${index}`} className="flex flex-col gap-0.5">
            <b>{classSet ? tp('class.history', { to: classSet.to ?? none }) : what(entry, t)}</b>
            <span className="text-muted-foreground">
              {[formatWhen(entry.at), entry.actor.displayName].join(' · ')}
              {entry.action === 'status-set' && entry.note ? ` — «${entry.note}»` : null}
              {classSet ? ` — ${tp('class.historyFrom', { from: classSet.from ?? none })}` : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * «Storia» (the board's card): `GET /v1/expenses/{id}/history`, oldest first, one line per event:
 * what happened in bold, then when and who. A status set by hand shows the new status and its
 * reason; an update lists the changed fields by their labels. `revision` reloads it after a write.
 */
export function ExpenseHistory({
  expenseId,
  revision,
}: {
  readonly expenseId: string;
  readonly revision: string;
}) {
  const { t } = useTranslation('expenses');
  const [state, setState] = useState<HistoryState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    apiClient
      .GET('/v1/expenses/{expenseId}/history', { params: { path: { expenseId } } })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', items: data.items });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [expenseId, revision]);

  return (
    <section aria-labelledby={`history-${expenseId}`} className="flex flex-col gap-2">
      <h3 id={`history-${expenseId}`} className="text-sm font-semibold">
        {t('detail.history.title')}
      </h3>
      <HistoryBody state={state} t={t} />
    </section>
  );
}

type T = ReturnType<typeof useTranslation<'expenses'>>['t'];

/** The fields an update may name, with a label of their own; any other reads as it came. */
const FIELD_NAMES = [
  'ownerUserId',
  'date',
  'description',
  'category',
  'total',
  'nonTaxable',
  'currency',
  'supplierName',
  'supplierVat',
  'documentType',
  'documentNumber',
  'documentStatus',
  'expectedDocumentNote',
  'payer',
  'paymentMethod',
  'traceablePayment',
  'businessReason',
  'peopleInvolved',
  'place',
  'taxLines',
  'internalClass',
] as const;
type FieldName = (typeof FIELD_NAMES)[number];
const isFieldName = (field: string): field is FieldName =>
  (FIELD_NAMES as readonly string[]).includes(field);

function what(entry: HistoryEntry, t: T): string {
  switch (entry.action) {
    case 'created':
      return t('detail.history.created');
    case 'updated': {
      const fields = (entry.fields ?? []).map((field) =>
        isFieldName(field) ? t(`detail.history.fields.${field}`) : field,
      );
      return fields.length > 0
        ? t('detail.history.updatedFields', { fields: fields.join(', ') })
        : t('detail.history.updated');
    }
    case 'status-set':
      return entry.status
        ? t('detail.history.statusSet', {
            status: t(`list.status.${entry.status}`),
          })
        : t('detail.history.statusChanged');
    case 'document-attached':
      return t('detail.history.documentAttached');
    case 'document-replaced':
      return t('detail.history.documentReplaced');
    case 'deleted':
      return t('detail.history.deleted');
    default:
      return String((entry as { action: unknown }).action);
  }
}
